import React, { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useForecast } from '../../hooks/useForecast'
import { useLibraryFunctions } from '../../hooks/useLibraryFunctions'
import { useUsers } from '../../hooks/useUsers'
import { ApiClient } from '../../api'
import { C, F } from './theme'
import { Card, DeptChip, Icon, Pill, Btn, Avatar } from './prims'
import type { LibraryFunction, ForecastTeamRow } from '../../types'

// EM-1 · "The Approval Team for this Idea". Rendered at the foot of the Team &
// approval tab (under the Delivery timeline). The function-library card view is
// the PRIMARY assignment surface: pick a person against a function → they join
// the delivery team and (EM-2) get emailed a confirm / comment / red-alert link.
// Assigning an "Executive Sign off" function also seeds the change-notice
// sign-off, so we refresh that panel when one is assigned.

const APPROVAL_STYLE: Record<string, { bg: string; fg: string; label: string }> = {
  pending: { bg: C.amberSoft, fg: C.amber, label: 'Pending' },
  approved: { bg: C.greenBg, fg: C.green, label: 'Approved' },
  blocked: { bg: C.oxbloodSoft, fg: C.oxblood, label: 'Blocked' }
}
const RESPONSE_STYLE: Record<string, { bg: string; fg: string; label: string; icon: string }> = {
  confirmed: { bg: C.greenBg, fg: C.green, label: 'Confirmed', icon: 'check' },
  commented: { bg: C.slateSoft, fg: C.slate, label: 'Commented', icon: 'msg' },
  'red-alert': { bg: C.oxbloodSoft, fg: C.oxblood, label: 'Red alert', icon: 'alert' }
}

const selectStyle: React.CSSProperties = {
  fontFamily: F.body, fontSize: 12.5, fontWeight: 600, color: C.ink, background: C.card,
  border: `1px solid ${C.line}`, borderRadius: 8, padding: '7px 9px', minWidth: 0, flex: 1
}

export function ApprovalTeamPanel({ eventId }: { eventId: number }) {
  const { fc, addMember, removeMember } = useForecast(eventId)
  const { functions } = useLibraryFunctions()

  if (!fc) return null
  const team = fc.team

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 4 }}>
        <span style={{ display: 'inline-flex', width: 26, height: 26, borderRadius: 7, alignItems: 'center', justifyContent: 'center', background: C.greenBg }}>
          <Icon name="shield" size={15} color={C.green} />
        </span>
        <span style={{ fontFamily: F.serif, fontSize: 19, fontWeight: 800, color: C.ink }}>The Approval Team for this Idea</span>
      </div>
      <div style={{ fontFamily: F.body, fontSize: 12.5, color: C.inkSoft, margin: '0 0 14px 35px' }}>
        Assign a person to each function. They join the delivery team and are emailed a link to confirm, comment, or raise a red alert.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(320px,1fr))', gap: 16 }}>
        {functions.map((f) => (
          <FunctionCard key={f.id} fn={f} assigned={team.filter((t) => t.functionId === f.id)} eventId={eventId} addMember={addMember} removeMember={removeMember} />
        ))}
      </div>
    </div>
  )
}

function FunctionCard({ fn, assigned, eventId, addMember, removeMember }: {
  fn: LibraryFunction
  assigned: ForecastTeamRow[]
  eventId: number
  addMember: (body: { personId: number; functionId: number; commitmentText?: string | null }) => Promise<unknown>
  removeMember: (rowId: number) => Promise<unknown>
}) {
  const { data: users } = useUsers()
  const qc = useQueryClient()
  const [personId, setPersonId] = useState('')
  const [busy, setBusy] = useState(false)
  const [resend, setResend] = useState<Record<number, 'sending' | 'sent' | 'failed'>>({})
  const isExec = fn.kind === 'exec-signoff'

  const onResend = async (rowId: number) => {
    setResend((s) => ({ ...s, [rowId]: 'sending' }))
    try { const r = await ApiClient.resendFunctionLink(rowId); setResend((s) => ({ ...s, [rowId]: r.ok ? 'sent' : 'failed' })) }
    catch { setResend((s) => ({ ...s, [rowId]: 'failed' })) }
  }

  // Don't offer people already on this function.
  const takenIds = new Set(assigned.map((a) => a.personId))
  const options = (users ?? []).filter((u) => !takenIds.has(u.id))

  const onAssign = async () => {
    const pid = Number(personId)
    if (!pid || busy) return
    setBusy(true)
    try {
      await addMember({ personId: pid, functionId: fn.id, commitmentText: fn.defaultResponsibilities[0] ?? null })
      setPersonId('')
      // Exec sign-off also creates a change-notice signatory — refresh that panel.
      if (isExec) qc.invalidateQueries({ queryKey: ['change-notice', eventId] })
    } catch { /* surfaced by mutation */ } finally { setBusy(false) }
  }

  return (
    <Card style={isExec ? { borderColor: C.brassLt, borderLeft: `3px solid ${C.brass}`, background: C.brassBg } : undefined}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <DeptChip id={fn.dept} />
        {isExec && <Pill fg={C.brassDk} bg={C.brassSoft} icon="star">Executive sign-off</Pill>}
        <span style={{ marginLeft: 'auto' }}>
          <Pill fg={C.inkSoft} bg={C.paper}>{assigned.length} assigned</Pill>
        </span>
      </div>
      <div style={{ fontFamily: F.serif, fontSize: 18, fontWeight: 700, color: C.ink, lineHeight: 1.15 }}>{fn.title}</div>

      {fn.defaultResponsibilities.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 10 }}>
          {fn.defaultResponsibilities.map((r, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 7 }}>
              <span style={{ marginTop: 2 }}><Icon name="check" size={12} color={isExec ? C.brass : C.green} /></span>
              <span style={{ fontFamily: F.body, fontSize: 12.5, color: C.inkSoft }}>{r}</span>
            </div>
          ))}
        </div>
      )}

      {/* Assigned people */}
      <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${C.lineSoft}`, display: 'flex', flexDirection: 'column', gap: 7 }}>
        {assigned.length === 0 ? (
          <div style={{ fontFamily: F.body, fontSize: 12, color: C.inkFaint }}>No one assigned yet.</div>
        ) : assigned.map((a) => {
          const ap = APPROVAL_STYLE[a.approvalStatus] ?? APPROVAL_STYLE.pending!
          const rs = a.responseStatus ? RESPONSE_STYLE[a.responseStatus] : null
          const st = resend[a.id]
          return (
            <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Avatar name={a.personName} size={26} />
              <span style={{ fontFamily: F.body, fontSize: 13, fontWeight: 700, color: C.ink, minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.personName}</span>
              {rs && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 7px', borderRadius: 999, background: rs.bg, color: rs.fg, fontFamily: F.body, fontSize: 10.5, fontWeight: 800 }}>
                  <Icon name={rs.icon} size={10} />{rs.label}
                </span>
              )}
              <span style={{ padding: '2px 8px', borderRadius: 999, background: ap.bg, color: ap.fg, fontFamily: F.body, fontSize: 10.5, fontWeight: 800 }}>{ap.label}</span>
              <button type="button" onClick={() => onResend(a.id)} disabled={st === 'sending'} title="Email the confirm link"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 3, border: 'none', background: 'transparent', cursor: 'pointer', color: st === 'sent' ? C.green : st === 'failed' ? C.oxblood : C.inkFaint, padding: 2, fontFamily: F.body, fontSize: 10, fontWeight: 700 }}>
                <Icon name="send" size={12} />{st === 'sent' ? 'Sent' : st === 'failed' ? 'No email' : st === 'sending' ? '…' : ''}
              </button>
              <button type="button" onClick={() => { removeMember(a.id).catch(() => {}) }} title="Remove" style={{ display: 'inline-flex', border: 'none', background: 'transparent', cursor: 'pointer', color: C.inkFaint, padding: 2 }}>
                <Icon name="x" size={13} />
              </button>
            </div>
          )
        })}
      </div>

      {/* Assign a person */}
      <div style={{ marginTop: 10, display: 'flex', gap: 7, alignItems: 'center' }}>
        <select value={personId} onChange={(e) => setPersonId(e.target.value)} style={selectStyle} disabled={options.length === 0}>
          <option value="">{options.length === 0 ? 'Everyone assigned' : 'Assign a person…'}</option>
          {options.map((u) => <option key={u.id} value={u.id}>{u.name ?? `User ${u.id}`}</option>)}
        </select>
        <Btn variant={isExec ? 'brass' : 'primary'} small icon="plus" onClick={onAssign} disabled={!personId || busy}>{busy ? '…' : 'Assign'}</Btn>
      </div>
    </Card>
  )
}
